'use client';

import { normalizeAppRoles } from '@/utils/app-roles.util';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ICompanyDetail, IUser, ProviderProps, QueryType } from '../types/types';
import { getUserRequest } from '../utils/handlers/client/user';
import { requestHandler } from '../utils/requestHandler';
export const dynamic = 'force-dynamic';

// TODO: Consider adding automatic user loading and a 'ready' flag to the context for more robust permission checks and user-dependent hooks.

// Create a context to manage Users-related data and functions
const UserContext = createContext({
  isUserLoading: false,
  roles: [],
  customFieldDefinitions: null,
  user: null,
  isInternalUser: false,
  hasNoUser: false,
  hasNoRolesInSelectedCompany: false,
  getUser: _data => {},
  clearUser: () => {},
  companyDetails: {}
});

// Create a hook to access the UserContext
const useUserContext = () => useContext(UserContext);

// Create a component
const UserProvider = ({
  children
}) => {
  const [user, setUser] = useState(null);
  const [hasNoUser, setHasNoUser] = useState(false);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState(null);
  const [roles, setRoles] = useState([]);
  const [hasNoRolesInSelectedCompany, setHasNoRolesInSelectedCompany] = useState(false);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const isInternalUser = useMemo(() => Boolean(user?.email?.toLowerCase().endsWith('@intalent.ai')), [user?.email]);
  const getUser = data => {
    requestHandler(async () => getUserRequest(data), res => {
      const {
        data
      } = res;
      if (data && Object.keys(data).length > 0) {
        const {
          roles,
          customFieldDefinitions,
          companyDetails,
          ...userData
        } = data;
        const normalizedRoles = normalizeAppRoles(roles);
        setUser(userData);
        setHasNoUser(Object.keys(userData).length === 0 ? true : false);
        setRoles(normalizedRoles);
        setHasNoRolesInSelectedCompany(Object.keys(userData).length > 0 && normalizedRoles.length === 0);
        setCustomFieldDefinitions(customFieldDefinitions);
        setCompanyDetails(companyDetails);
      } else {
        setUser(null);
        setHasNoUser(true);
        setRoles([]);
        setHasNoRolesInSelectedCompany(false);
      }
    }, _errorMessage => {
      // sshowErrorToast(errorMessage)
    }, setIsUserLoading);
  };
  const clearUser = useCallback(() => {
    setUser(null);
    setRoles([]);
    setHasNoRolesInSelectedCompany(false);
    setIsUserLoading(false);
    sessionStorage.clear();
    localStorage.clear();
  }, []);

  // Provide Users-related data and functions through the context
  return <UserContext.Provider value={{
    getUser,
    isUserLoading,
    roles,
    user,
    isInternalUser,
    hasNoUser,
    hasNoRolesInSelectedCompany,
    clearUser,
    customFieldDefinitions,
    companyDetails
  }}>
            {children}
        </UserContext.Provider>;
};

// Export the context, provider component, and custom hook
export { UserContext, UserProvider, useUserContext };